import type { AgentSettingsTab } from './types';

/**
 * Single registry for the Settings hub tabs.
 *
 * The tab lives in the URL (`.../settings/:tab`) so the screen supports
 * deep-links, refresh and back/forward — a requirement of PRD Feature 8.1.
 * Order is fixed by the PRD; the Segmented bar, the URL guard and the sidebar
 * all read it from here, so adding a tab is a single edit.
 */
export const AGENT_SETTINGS_TABS: readonly AgentSettingsTab[] = [
  'personality',
  'behavior',
  'whitelist',
  'billing',
];

/** Tab used when the URL carries none, or one that doesn't exist. */
export const DEFAULT_AGENT_SETTINGS_TAB: AgentSettingsTab = 'personality';

/**
 * A process agent's Settings (SL-2032): General, Whitelist, Billing, in that
 * order. Personality and Behavior do not exist for it — not even disabled — and
 * a saved link to either lands on General.
 */
export const PROCESS_AGENT_SETTINGS_TABS: readonly AgentSettingsTab[] = [
  'general',
  'whitelist',
  'billing',
];

/** Where a process agent's Settings opens (Agent's Home card, module menu, bare URL). */
export const DEFAULT_PROCESS_AGENT_SETTINGS_TAB: AgentSettingsTab = 'general';

/** Translation key (`agent-settings` namespace) of the label in the Segmented bar. */
export const AGENT_SETTINGS_TAB_LABEL_KEY = {
  general: 'tabs.general',
  personality: 'tabs.personality',
  behavior: 'tabs.behavior',
  whitelist: 'tabs.whitelist',
  billing: 'tabs.billing',
} as const satisfies Record<AgentSettingsTab, string>;

/** Translation key of the header subtitle shown while each tab is open. */
export const AGENT_SETTINGS_TAB_SUBTITLE_KEY = {
  general: 'subtitles.general',
  personality: 'subtitles.personality',
  behavior: 'subtitles.behavior',
  whitelist: 'subtitles.whitelist',
  billing: 'subtitles.billing',
} as const satisfies Record<AgentSettingsTab, string>;

/** Narrows a raw URL segment to a known tab, of either agent kind. */
export function isAgentSettingsTab(value: string | undefined): value is AgentSettingsTab {
  return (
    value !== undefined &&
    [...AGENT_SETTINGS_TABS, ...PROCESS_AGENT_SETTINGS_TABS].includes(value as AgentSettingsTab)
  );
}

/**
 * Sidebar submenu key for a Settings tab. Derived rather than restated so the
 * menu's highlighting can't drift from the hub's tab list.
 */
export function agentSettingsTabMenuKey(tab: AgentSettingsTab): string {
  return `settings-${tab}`;
}

/** The tab a Settings URL points at, or the default when it names none. */
export function agentSettingsTabFromPath(pathname: string): AgentSettingsTab {
  const segment = pathname.split('/').filter(Boolean).pop();
  return isAgentSettingsTab(segment) ? segment : DEFAULT_AGENT_SETTINGS_TAB;
}

/** Absolute path of a Settings tab for a given agent. */
export function agentSettingsTabPath(
  accountId: string,
  assistantId: string,
  tab: AgentSettingsTab,
): string {
  return `/accounts/${accountId}/assistants/${assistantId}/settings/${tab}`;
}
